export class PerfectShotCases extends HTMLElement {
    FIVE_MINUTE_POLL_URL = "/perfect-shot/cases/list";
    WARNING_FAIL_STATUS = 'warning_fail';
    ALL_STATUS = 'all';
    currentStatusFilter = this.WARNING_FAIL_STATUS;

    constructor() {
        super();
        this.searchTerm = '';

    }

    connectedCallback() {
        this.init();
    }

    init() {
        this.initEventListeners();

        // in init(); capture:true because error events don't bubble
        this.addEventListener('error', event => {
            if (event.target.matches('.patient-thumbnail img')) {
                event.target.remove();
            }
        }, true);

        // sweep imgs that already failed before this listener attached
        this.querySelectorAll('.patient-thumbnail img').forEach(img => {
            if (img.complete && img.naturalWidth === 0) {
                img.remove();
            }
        });

        this.toolbar.querySelector('.ps-search input').value = ''; 

        this.refreshPager();
    }

    get toolbar() { return this.closest('.v2-page').querySelector('.v2-page__toolbar'); }

    initEventListeners() {
        this.addEventListener('pager:page-change', () => this.turnPage());
        this.addEventListener('pager:per-page-change', () => this.turnPage());
        // toolbar search/filter handlers (§7.2 #2) call refreshPager() after re-marking rows
        this.toolbar.addEventListener('click', event => {
            const button = event.target.closest('.ps-filter button');
            if (!button) {
                return;
            }
            this.applyFilter(button);
        });

        this.toolbar.addEventListener('input', event => {
            if (event.target.matches('.ps-search input')) {
                this.applySearch(event.target.value);
            }
        });

        this.addEventListener('click', event => {
            const button = event.target.closest('.action-button');
            if (button) {
                this.reviewDialog?.open(button.closest('tr.ps-row').dataset.case_id);
            }
        });

        document.body.addEventListener("five-minute-mark", () => this.fiveMinuteCheck());
        document.body.addEventListener('perfect-shot-review-closed', () => this.fiveMinuteCheck());
        // one path, two producers: the review dialog and 26978's image-viewer panel
        document.body.addEventListener('perfect-shot-case-reviewed', event => this.handleCaseReviewed(event));
    }

    applyFilter(button) {
        this.currentStatusFilter = button.dataset.filter;
        this.toolbar.querySelector('.ps-filter button.active')?.classList.remove('active');
        button.classList.add('active');
        this.applyRowVisibility();
    }

    applySearch(term) {
        this.searchTerm = term.trim().toLowerCase();
        this.applyRowVisibility();
    }

    applyRowVisibility(keepPage = false) {
        this.allRows.forEach(row => {
            const matchesStatus = this.currentStatusFilter === this.ALL_STATUS
                || row.classList.contains('ps-row--fail')
                || row.classList.contains('ps-row--warning');

            const matchesSearch = this.searchTerm === ''
                || row.dataset.patient_name.toLowerCase().includes(this.searchTerm)
                || row.dataset.case_id.includes(this.searchTerm);

            row.classList.toggle('filtered-out', !(matchesStatus && matchesSearch));
        });

        this.refreshPager(keepPage);
    }


    /**
     * The dialog owns the POST; we own the row.  Everything here mirrors
     * ps_cases__table_row.blade.php's `is_reviewed` branches, so a local update and the next
     * server-rendered row agree.
     */
    handleCaseReviewed(event) {
        const { case_id: caseId, fingerprint } = event.detail ?? {};
        const row = this.querySelector(`tr.ps-row[data-case_id="${caseId}"]`);
        if (!row) {
            return;                     // reviewed from the image manager, not from this list
        }

        // opacity + the neutral left stripe both hang off this one class swap
        row.classList.remove('unreviewed');
        row.classList.add('reviewed');

        // Verbatim, never composed here (§7.2 #4) — without it the next poll sees this row as
        // changed and re-sends it forever.
        if (fingerprint) {
            row.dataset.fingerprint = fingerprint;
        }

        row.querySelector('td.patient span.quality-indicator')?.remove();

        const action = row.querySelector('td.action div.action-content');
        if (action) {
            action.innerHTML = '<vetology-svg data-svg="check_circle"></vetology-svg><span>Reviewed</span>';
        }

        // No applyRowVisibility() — neither the status filter nor the search reads reviewed
        // state, so a review can never change which rows match.
        this.broadcastUnreviewedCount();
    }

    /** the DOM mirror of stats.unreviewed (PerfectShotService.php:411): warning/fail, not reviewed */
    unreviewedCount() {
        return this.allRows.filter(row => row.classList.contains('unreviewed')
            && (row.classList.contains('ps-row--warning') || row.classList.contains('ps-row--fail'))
        ).length;
    }

    /**
     * Two indicators, one number.  PerfectShotMonitor never hears 'perfect-shot-case-reviewed'
     * (PerfectShotMonitor.js:12) — the sidebar badge only moves on the event below, so this
     * element is the translator.  The header chip is ours to paint directly.
     * Callers with a server count pass it in; the DOM recount is the local-review path.
     */
    broadcastUnreviewedCount(count = this.unreviewedCount()) {
        document.body.dispatchEvent(new CustomEvent('perfect-shot-unreviewed-changed', {
            detail: { count }
        }));

        const chip = document.querySelector('.ps-unreviewed-chip');
        if (!chip) {
            return;
        }
        chip.dataset.count = count;                 // red -> green keys off [data-count="0"]
        chip.querySelector('.unreviewed-text').textContent = `${count} unreviewed issues`;

        // VetologySvg replaces itself with the <svg> once fetched, carrying data-svg across, so
        // [data-svg] matches either form and swapping means a fresh element — guarded, because
        // a review that leaves the count above zero must not churn the icon.
        const icon = chip.querySelector('[data-svg]');
        const wanted = count > 0 ? 'warning' : 'check_circle';
        if (icon && icon.dataset.svg !== wanted) {
            const replacement = document.createElement('vetology-svg');
            replacement.dataset.svg = wanted;
            replacement.setAttribute('width', '16');
            replacement.setAttribute('height', '16');
            icon.replaceWith(replacement);
        }
    }


    /** user-initiated page turns only — refreshPager()/init must NOT scroll */
    turnPage() {
        this.renderPage();
        this.querySelector('table.ps-cases').scrollIntoView({ block: 'start' });
    }

    get pager() { return this.querySelector('vetology-pager'); }
    get reviewDialog() { return document.querySelector('perfect-shot-review-dialog'); }
    get allRows() { return [...this.querySelectorAll('tbody tr.ps-row')]; }

    /** rows surviving the active filter + search — the pagination universe */
    get matchedRows() { return this.allRows.filter(row => !row.classList.contains('filtered-out')); }

 
    renderPage() {
        const perPage = parseInt(this.pager.getAttribute('per-page')) || 25;
        const first = (parseInt(this.pager.getAttribute('page')) - 1) * perPage;
        this.matchedRows.forEach((row, index) => {
            row.hidden = index < first || index >= first + perPage;
        });
    }

    fiveMinuteCheck()
    {
        if (this.reviewDialog?.isOpen) {
            return;
        }

        // build an array of objects keyed by case_id, each containing analysis_run_key and reviewed status
        const data = new FormData();
        const caseFingerprints = {};
        this.querySelectorAll('tbody tr.ps-row').forEach(row => {
            caseFingerprints[row.dataset.case_id] = row.dataset.fingerprint;
        });
        data.append('case_fingerprints', JSON.stringify(caseFingerprints));

        fetch(this.FIVE_MINUTE_POLL_URL,
            {
                method: "POST",
                body: data,
                headers: { Accept: "application/json" }
            }
        ).then(response => (response.ok ? response.json() : null))
            .then(data => {
                if (data && data.success) {
                    this.updateSummaryBar(data.stats);
                    this.broadcastUnreviewedCount(data.stats.unreviewed);
                    if (data.show_clinic !== (this.querySelector('thead th.col-clinic') !== null)) {
                        window.location.reload();
                        return;
                    }
                    this.updateCaseRows(data.rows, data.removed, data.order);
                }
            })
            .catch(error => {
                console.error("Error fetching Perfect Shot case results:", error);
            });
    }

    updateSummaryBar(stats)
    {
        // Implementation for updating the summary bar with stats
        const summaryBar = document.querySelector('.v2-page__summary');

        const scTotal = summaryBar.querySelector('.summary-card.total .card-count');
        if (scTotal) {
            scTotal.textContent = stats.total;
        }

        const scPass = summaryBar.querySelector('.summary-card.pass .card-count');
        if (scPass) {
            scPass.textContent = stats.passed;
        }

        const scWarning = summaryBar.querySelector('.summary-card.warning .card-count');
        if (scWarning) {
            scWarning.textContent = stats.warnings;
        }

        const scFail = summaryBar.querySelector('.summary-card.fail .card-count');
        if (scFail) {
            scFail.textContent = stats.failures;
        }

    }

    /** call once from init() and after every filter/search change */
    refreshPager(keepPage = false) {
        const perPage = parseInt(this.pager.getAttribute('per-page')) || 25;
        const lastPage = Math.max(1, Math.ceil(this.matchedRows.length / perPage));
        const page = keepPage
            ? Math.min(parseInt(this.pager.getAttribute('page')) || 1, lastPage)
            : 1;

        this.pager.setAttribute('total-items', this.matchedRows.length);
        this.pager.setAttribute('page', page);
        this.renderPage();
    }

    updateCaseRows(rows, removed, order) {
        const tbody = this.querySelector('tbody');

        if (order.length > 0) {
            tbody.querySelector('td.no-results')?.closest('tr').remove();
        }

        removed.forEach(caseId => {
            this.querySelector(`tr.ps-row[data-case_id="${caseId}"]`)?.remove();
        });

        // rows already in the table…
        const byCaseId = new Map(this.allRows.map(row => [row.dataset.case_id, row]));

        // …plus the changed/new ones, parsed but deliberately left OUT of the document:
        // inserting here and moving them below would fire connectedCallback twice on
        // every <vetology-svg> inside (VetologySvg self-replaces on an async fetch).
        Object.entries(rows).forEach(([caseId, html]) => {
            const template = document.createElement('template');
            template.innerHTML = html.trim();

            byCaseId.get(caseId)?.remove();
            byCaseId.set(caseId, template.content.firstElementChild);
        });

        let cursor = tbody.firstElementChild;

        order.forEach(caseId => {
            const row = byCaseId.get(String(caseId));
            if (!row) {
                return;
            }
            if (row === cursor) {
                cursor = cursor.nextElementSibling;   // already in place — no DOM write
            } else {
                tbody.insertBefore(row, cursor);      // detached node → its first and only insert
            }
        });

        this.applyRowVisibility(true);
    }
}
    

customElements.define('perfect-shot-cases', PerfectShotCases);